import { type DateValue, getLocalTimeZone, today } from "@internationalized/date";
import { useNavigate } from "@tanstack/react-router";
import { ArrowUpRight, BarChartSquare02, CurrencyDollar, CurrencyPound, InfoCircle, Users01 } from "@untitledui/icons";
import React, { useState } from "react";
import { Bar, BarChart as RechartsBarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { MetricChangeIndicator, MetricsChart01 } from "@/components/application/metrics/metrics";
import { Table, TableCard } from "@/components/application/table/table";
import { Tooltip as BaseTooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { useCurrency } from "@/hooks/use-currency";
import { useDashboardData } from "@/hooks/use-dashboard-data";
import { CreatorDetails } from "@/pages/creators/creator-details";
import { ProductDetails } from "@/pages/products/product-details";
import { useMerchant } from "@/providers/MerchantProvider";
import { cx } from "@/utils/cx";
import { dateRangeToApiFormat } from "@/utils/date-conversion";
import { formatCurrency as utilFormatCurrency, formatNumber as utilFormatNumber } from "@/utils/formatNumber";
import { buildKpiTooltipParts } from "@/utils/kpi-tooltip";
import { hasValidPriorPeriodComparison } from "@/utils/prior-period";
import { useCurrentCurrency } from "@/utils/useCurrentCurrency";
import { CreatorsLoadingState, RevenueLoadingState, WeeklyKPIsLoadingState } from "./AnalyticsLoadingStates";

export interface ChartDataPoint {
  week: string;
  value: number;
}

interface KPICardProps {
  title: string;
  subtitle: string;
  change: string;
  changeTrend: "positive" | "negative";
  chartData: ChartDataPoint[];
  footer?: React.ReactNode;
  className?: string;
  metricType?: "unit" | "gmv" | "video" | "percent";
  metricName?: string;
  currentPeriod?: {
    from: string;
    to: string;
    value: number;
  };
  priorPeriod?: {
    from: string;
    to: string;
    value: number;
  };
  timePeriod?: "daily" | "weekly" | "monthly";
  dateRange?: { start: DateValue; end: DateValue } | null;
  limits?: { minDate: DateValue; maxDate: DateValue };
  formatValue?: (value: number) => string;
}

const MetricsTooltip: React.FC<{
  metricType?: "unit" | "gmv" | "video" | "percent";
  metricName?: string;
  staticTT?: boolean;
  chartData?: ChartDataPoint[];
  chartContainerRef?: React.RefObject<HTMLDivElement | null>;
}> = ({ metricType = "gmv", metricName = "Value", staticTT = false, chartData, chartContainerRef }) => {
  const currency = useCurrency();

  // Custom tooltip content with positioning logic
  const CustomTooltipContent = ({ active, payload, label, coordinate, viewBox }: any) => {
    if (!active || !payload || !payload.length) return null;

    const tooltipWidth = 200;
    const viewportWidth = window.innerWidth;

    // Get the chart's position on screen using the ref
    let absoluteX = coordinate?.x || 0;
    if (chartContainerRef?.current) {
      const chartRect = chartContainerRef.current.getBoundingClientRect();
      absoluteX = chartRect.left + (coordinate?.x || 0);
    }

    // Check if tooltip would overflow viewport on the right
    const shouldShowLeft = absoluteX + tooltipWidth + 10 > viewportWidth - 20;

    // Calculate spacing from chart data
    const chartWidth = viewBox?.width || 0;
    const dataLength = chartData?.length || 1;
    // Total available width per data point (includes bar + gaps)
    const widthPerPoint = chartWidth / dataLength;
    // coordinate.x is at center of bar. To get tooltip's right edge at bar's left edge:
    // - Need to move left by half of the space allocated to this data point
    // - Then add gap
    const offsetFromCenter = widthPerPoint / 2 + 10;

    const value = payload[0].value;
    let formattedValue: string;
    switch (metricType) {
      case "unit":
        formattedValue = `${value}`;
        break;
      case "gmv":
        formattedValue = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency,
          notation: "compact",
          compactDisplay: "short",
          maximumFractionDigits: 2,
        }).format(value);
        break;
      case "video":
        formattedValue = new Intl.NumberFormat("en-US", {
          compactDisplay: "short",
          notation: "compact",
        }).format(value);
        break;
      case "percent":
        formattedValue = new Intl.NumberFormat("en-US", {
          style: "percent",
          compactDisplay: "short",
          notation: "compact",
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }).format(value);
        break;
      default:
        formattedValue = value.toString();
    }

    return (
      <div
        className="bg-primary-solid rounded-lg px-3 py-3 flex flex-col gap-1 max-w-xs shadow-none"
        style={{
          transform: shouldShowLeft ? `translateX(calc(-100% - ${offsetFromCenter}px))` : undefined,
          pointerEvents: "none",
        }}
      >
        <span className="text-xs font-semibold text-white">{label}</span>
        <span className="text-xs font-medium text-tooltip-supporting-text">
          {metricName}: {formattedValue}
        </span>
      </div>
    );
  };

  return (
    <Tooltip
      wrapperStyle={{
        zIndex: 1000,
      }}
      content={<CustomTooltipContent />}
      offset={10}
      position={staticTT ? { x: -80, y: -40 } : undefined}
      allowEscapeViewBox={{ x: true, y: true }}
    />
  );
};

const KPICard: React.FC<KPICardProps> = ({
  title,
  subtitle,
  change,
  changeTrend,
  chartData,
  footer,
  className,
  metricType = "unit",
  metricName = "Value",
  currentPeriod,
  priorPeriod,
  timePeriod = "weekly",
  dateRange,
  limits,
  formatValue,
}) => {
  const chartContainerRef = React.useRef<HTMLDivElement>(null);

  const formatPriorPeriodDate = (dateStr: string) => {
    const date = /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
      ? (() => {
          const [y, m, d] = dateStr.split("-").map(Number);
          return new Date(y, m - 1, d);
        })()
      : new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const isValidComparison = hasValidPriorPeriodComparison(priorPeriod, dateRange, limits);

  return (
    <div className={cx("bg-white rounded-lg flex flex-col", className)}>
      <div className="flex-1 px-6 pt-8 pb-6 flex flex-col gap-5">
        <div className="text-tertiary text-base font-medium">{subtitle}</div>

        <div className="flex flex-col gap-1.5">
          <div className="text-primary text-5xl font-medium leading-[60px]">{title}</div>
          <div className="inline-flex justify-start items-center gap-2">
            <MetricChangeIndicator type="trend" trend={changeTrend} value={change} disabled={!isValidComparison} />
            <span className="text-tertiary text-sm font-medium">
              {isValidComparison ? "vs prior period" : "Prior period unavailable"}
            </span>
            <BaseTooltip
              {...(isValidComparison && currentPeriod && priorPeriod
                ? buildKpiTooltipParts({
                    metricName: metricName ?? "Value",
                    currentPeriod,
                    priorPeriod,
                    change,
                    formatValue: formatValue ?? ((v) => utilFormatNumber(v, true)),
                  })
                : {
                    title: isValidComparison ? "Prior period" : "Prior period unavailable",
                    description:
                      isValidComparison && priorPeriod ? (
                        <>
                          {formatPriorPeriodDate(priorPeriod.from)} - {formatPriorPeriodDate(priorPeriod.to)}
                          <br />
                          {metricName} = {(formatValue ?? ((v) => utilFormatNumber(v, true)))(priorPeriod.value)}
                        </>
                      ) : (
                        "Comparison requires at least 2x the selected period. Choose a smaller date range."
                      ),
                  })}
              placement="top"
            >
              <TooltipTrigger
                isDisabled={false}
                className="inline-flex items-center justify-center cursor-help transition-colors hover:text-gray-500"
              >
                <InfoCircle className="w-4 h-4 text-gray-400" />
              </TooltipTrigger>
            </BaseTooltip>
          </div>
        </div>

        <div className="h-[147px]" ref={chartContainerRef}>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsBarChart
              key={timePeriod}
              data={chartData}
              margin={{
                left: 8,
                right: 8,
                top: 8,
                bottom: 8,
              }}
            >
              <XAxis dataKey="week" hide={true} />
              <MetricsTooltip
                metricType={metricType}
                metricName={metricName}
                chartData={chartData}
                chartContainerRef={chartContainerRef}
              />
              <Bar isAnimationActive={false} dataKey="value" fill="#5d3b81" radius={[2, 2, 0, 0]} maxBarSize={20} />
            </RechartsBarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {footer && <div className="px-6 pb-6 flex items-center justify-end">{footer}</div>}
    </div>
  );
};

const WeeklyKPIsOverview: React.FC<{
  timePeriod: "daily" | "weekly" | "monthly";
  dateRange?: { start: DateValue; end: DateValue } | null;
  limits?: { minDate: DateValue; maxDate: DateValue };
}> = ({ timePeriod, dateRange, limits }) => {
  const apiDateRange = dateRangeToApiFormat(dateRange);

  const { data, isLoading } = useDashboardData(timePeriod, apiDateRange);
  const navigate = useNavigate();
  const merchant = useMerchant();
  const currencySymbol = useCurrentCurrency();

  if (isLoading) {
    return <WeeklyKPIsLoadingState />;
  }

  if (!data) {
    return (
      <div className={cx("w-full", "inline-flex gap-12 items-end")}>
        <div className="w-11/40 self-stretch inline-flex flex-col justify-start items-start">
          <div className="self-stretch flex-1 grid items-center text-primary text-3xl font-normal leading-[38px] px-2">
            No data available
          </div>
        </div>
        <div className="w-29/40 flex justify-start items-start gap-4">
          <div className="flex-1 text-center text-gray-500">No data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className={cx("w-full", "inline-flex gap-12 items-end")}>
      <div className="w-11/40 self-stretch inline-flex flex-col justify-center items-start">
        <div className="text-primary text-3xl font-normal leading-[38px] px-2">Core Performance Metrics</div>
      </div>
      <div className="w-29/40 flex justify-start items-start gap-4">
        <div className="flex-1">
          <KPICard
            title={data.mainKPIs.weeklyGMV.value}
            subtitle={data.mainKPIs.weeklyGMV.description}
            change={data.mainKPIs.weeklyGMV.change}
            changeTrend={data.mainKPIs.weeklyGMV.trend === "up" ? "positive" : "negative"}
            chartData={data.mainKPIs.weeklyGMV.chartData}
            metricType="gmv"
            metricName="GMV"
            currentPeriod={data.mainKPIs.weeklyGMV.currentPeriod}
            priorPeriod={data.mainKPIs.weeklyGMV.priorPeriod}
            formatValue={(v) => utilFormatCurrency(currencySymbol, v)}
            timePeriod={timePeriod}
            dateRange={dateRange}
            limits={limits}
            footer={
              <button
                type="button"
                onClick={() =>
                  navigate({ to: "/merchant/$merchantId/product-explorer", params: { merchantId: merchant } })
                }
                className="inline-flex justify-end items-center gap-2 hover:opacity-75 transition-opacity"
              >
                <span className="text-tertiary text-sm font-medium">Explore products</span>
                <ArrowUpRight className="w-6 h-6 text-tertiary" />
              </button>
            }
            className="h-[397px]"
          />
        </div>
        <div className="flex-1">
          <KPICard
            title={data.mainKPIs.weeklyVideos.value}
            subtitle={data.mainKPIs.weeklyVideos.description}
            change={data.mainKPIs.weeklyVideos.change}
            changeTrend={data.mainKPIs.weeklyVideos.trend === "up" ? "positive" : "negative"}
            chartData={data.mainKPIs.weeklyVideos.chartData}
            metricType="video"
            metricName="Posted"
            currentPeriod={data.mainKPIs.weeklyVideos.currentPeriod}
            priorPeriod={data.mainKPIs.weeklyVideos.priorPeriod}
            formatValue={(v) => utilFormatNumber(v, true)}
            timePeriod={timePeriod}
            dateRange={dateRange}
            limits={limits}
            footer={
              <button
                type="button"
                onClick={() =>
                  navigate({ to: "/merchant/$merchantId/video-explorer", params: { merchantId: merchant } })
                }
                className="inline-flex justify-end items-center gap-2 hover:opacity-75 transition-opacity"
              >
                <span className="text-tertiary text-sm font-medium">Explore videos</span>
                <ArrowUpRight className="w-6 h-6 text-tertiary" />
              </button>
            }
            className="h-[397px]"
          />
        </div>
        <div className="flex-1">
          <KPICard
            title={data.mainKPIs.weeklyCreators.value}
            subtitle={data.mainKPIs.weeklyCreators.description}
            change={data.mainKPIs.weeklyCreators.change}
            changeTrend={data.mainKPIs.weeklyCreators.trend === "up" ? "positive" : "negative"}
            chartData={data.mainKPIs.weeklyCreators.chartData}
            metricType="video"
            metricName="Creators"
            currentPeriod={data.mainKPIs.weeklyCreators.currentPeriod}
            priorPeriod={data.mainKPIs.weeklyCreators.priorPeriod}
            formatValue={(v) => utilFormatNumber(v, true)}
            timePeriod={timePeriod}
            dateRange={dateRange}
            limits={limits}
            footer={
              <button
                type="button"
                onClick={() =>
                  navigate({ to: "/merchant/$merchantId/creator-explorer", params: { merchantId: merchant } })
                }
                className="inline-flex justify-end items-center gap-2 hover:opacity-75 transition-opacity"
              >
                <span className="text-tertiary text-sm font-medium">Explore Brand creators</span>
                <ArrowUpRight className="w-6 h-6 text-tertiary" />
              </button>
            }
            className="h-[397px]"
          />
        </div>
      </div>
    </div>
  );
};

const RevenueOverview: React.FC<{
  timePeriod: "daily" | "weekly" | "monthly";
  dateRange?: { start: DateValue; end: DateValue } | null;
  limits?: { minDate: DateValue; maxDate: DateValue };
}> = ({ timePeriod, dateRange, limits }) => {
  const apiDateRange = dateRangeToApiFormat(dateRange);

  const { data, isLoading } = useDashboardData(timePeriod, apiDateRange);
  const currency = useCurrency();
  const currencySymbol = useCurrentCurrency();
  const [selectedProductId, setSelectedProductId] = useState<string | undefined>();

  if (isLoading) {
    return <RevenueLoadingState />;
  }

  if (!data) {
    return (
      <div
        className={cx(
          "w-full  px-8 py-6", // Min Max stretch width
          "bg-white rounded-lg", // Container aesthetic
          "justify-between items-start inline-flex flex-col gap-6", // Internal stretch behaviour
        )}
      >
        <div className="inline-flex justify-start items-center gap-2">
          {currency && currency === "GBP" ? (
            <CurrencyPound className="w-5 h-5 text-primary" />
          ) : (
            <CurrencyDollar className="w-5 h-5 text-primary" />
          )}
          <div className="text-primary text-base font-medium">Revenue</div>
        </div>
        <div className="self-stretch text-center text-gray-500">No data available</div>
      </div>
    );
  }

  return (
    <div
      className={cx(
        "w-full  px-8 py-6", // Min Max stretch width
        "bg-white rounded-lg", // Container aesthetic
        "justify-between items-start inline-flex flex-col gap-6", // Internal stretch behaviour
      )}
    >
      <div className="inline-flex justify-start items-center gap-2">
        {currency && currency === "GBP" ? (
          <CurrencyPound className="w-5 h-5 text-primary" />
        ) : (
          <CurrencyDollar className="w-5 h-5 text-primary" />
        )}
        <div className="text-primary text-base font-medium">Revenue</div>
      </div>
      <div className="self-stretch inline-flex justify-start gap-4">
        {" "}
        {/* Layout container */}
        <div className="flex-1 ">
          <TableCard.Root className={cx("self-start", "h-[364px]")} size="md">
            <Table aria-label="Top Products" selectionMode="none">
              <Table.Header className="h-44px">
                <Table.Head label="Top Products" className="w-2/5" isRowHeader />
                <Table.Head label="GMV" className="w-1/5" />
                <Table.Head label="% Total GMV" className="w-1/5" />
                <Table.Head label="Units sold" className="w-1/5" />
              </Table.Header>
              <Table.Body items={data.topProducts}>
                {(product) => {
                  const id = `product-${product.name}`;
                  return (
                    <Table.Row id={id} key={id} className="h-[64px]">
                      <Table.Cell className="text-sm font-medium text-primary">
                        <button
                          type="button"
                          onClick={() => setSelectedProductId(product.id)}
                          className="text-left underline decoration-dotted decoration-1 underline-offset-2 hover:text-brand-primary transition-colors cursor-pointer"
                        >
                          {product.name}
                        </button>
                      </Table.Cell>
                      <Table.Cell className="text-sm font-medium text-primary">{product.gmv}</Table.Cell>
                      <Table.Cell className="text-sm font-medium text-primary">{product.percentage}</Table.Cell>
                      <Table.Cell className="text-sm font-medium text-primary">{product.units}</Table.Cell>
                    </Table.Row>
                  );
                }}
              </Table.Body>
            </Table>
          </TableCard.Root>
        </div>
        <div className="w-[388px] self-stretch inline-flex flex-col justify-start items-start gap-4">
          {/* Chart container */}
          <MetricsChart01
            title={data.revenueKPIs.weeklyProductImpressions.value}
            subtitle={data.revenueKPIs.weeklyProductImpressions.description}
            change={data.revenueKPIs.weeklyProductImpressions.change}
            changeDescription="vs prior period"
            actions={false}
            trend={data.revenueKPIs.weeklyProductImpressions.trend === "up" ? "positive" : "negative"}
            chartData={data.revenueKPIs.weeklyProductImpressions.chartData?.map((item, index, array) => ({
              week: item.week,
              value: item.value,
              highlight: index === array.length - 1,
            }))}
            tooltip={<MetricsTooltip metricType="video" metricName="Views" staticTT={true} />}
            currentPeriod={data.revenueKPIs.weeklyProductImpressions.currentPeriod}
            priorPeriod={data.revenueKPIs.weeklyProductImpressions.priorPeriod}
            metricName="Views"
            formatValue={(v) => utilFormatNumber(v, true)}
            dateRange={dateRange}
            limits={limits}
            className="w-[388px] h-[174px]"
          />
          <MetricsChart01
            title={data.revenueKPIs.weeklyAOV.value}
            subtitle={data.revenueKPIs.weeklyAOV.description}
            change={data.revenueKPIs.weeklyAOV.change}
            changeDescription="vs prior period"
            actions={false}
            trend={data.revenueKPIs.weeklyAOV.trend === "up" ? "positive" : "negative"}
            chartData={data.revenueKPIs.weeklyAOV.chartData?.map((item, index, array) => ({
              week: item.week,
              value: item.value,
              highlight: index === array.length - 1,
            }))}
            tooltip={<MetricsTooltip metricType="gmv" metricName="AOV" staticTT={true} />}
            currentPeriod={data.revenueKPIs.weeklyAOV.currentPeriod}
            priorPeriod={data.revenueKPIs.weeklyAOV.priorPeriod}
            metricName="AOV"
            formatValue={(v) => utilFormatCurrency(currencySymbol, v)}
            dateRange={dateRange}
            limits={limits}
            className="w-[388px] h-[174px]"
          />
        </div>
      </div>

      <ProductDetails id={selectedProductId} onClose={() => setSelectedProductId(undefined)} />
    </div>
  );
};

const CreatorsOverview: React.FC<{
  timePeriod: "daily" | "weekly" | "monthly";
  dateRange?: { start: DateValue; end: DateValue } | null;
  limits?: { minDate: DateValue; maxDate: DateValue };
}> = ({ timePeriod, dateRange, limits }) => {
  const apiDateRange = dateRangeToApiFormat(dateRange);

  const { data, isLoading } = useDashboardData(timePeriod, apiDateRange);
  const currencySymbol = useCurrentCurrency();
  const [selectedCreatorId, setSelectedCreatorId] = useState<string | undefined>();

  if (isLoading) {
    return <CreatorsLoadingState />;
  }

  if (!data) {
    return (
      <div
        className={cx(
          "w-full  px-8 py-6", // Min Max stretch width
          "bg-white rounded-lg", // Container aesthetic
          "justify-between items-start inline-flex flex-col gap-6", // Internal stretch behaviour
        )}
      >
        <div className="inline-flex justify-start items-center gap-2">
          <BarChartSquare02 className="w-5 h-5 text-primary" />
          <div className="text-primary text-base font-medium">Creators</div>
        </div>
        <div className="self-stretch text-center text-gray-500">No data available</div>
      </div>
    );
  }

  return (
    <div
      className={cx(
        "w-full  px-8 py-6", // Min Max stretch width
        "bg-white rounded-lg", // Container aesthetic
        "justify-between items-start inline-flex flex-col gap-6", // Internal stretch behaviour
      )}
    >
      <div className="inline-flex justify-start items-center gap-2">
        <Users01 className="w-5 h-5 text-primary" />
        <div className="text-primary text-base font-medium">Creators</div>
      </div>
      <div className="self-stretch inline-flex justify-start gap-4">
        <div className="w-[388px] self-stretch inline-flex flex-col justify-start items-start gap-4">
          {/* Chart container */}
          <MetricsChart01
            title={data.creatorKPIs.weeklyNewCreators.value}
            subtitle={data.creatorKPIs.weeklyNewCreators.description}
            change={data.creatorKPIs.weeklyNewCreators.change}
            changeDescription="vs prior period"
            actions={false}
            trend={data.creatorKPIs.weeklyNewCreators.trend === "up" ? "positive" : "negative"}
            chartData={data.creatorKPIs.weeklyNewCreators.chartData?.map((item, index, array) => ({
              week: item.week,
              value: item.value,
              highlight: index === array.length - 1,
            }))}
            tooltip={<MetricsTooltip metricType="video" metricName="New Creators" staticTT={true} />}
            currentPeriod={data.creatorKPIs.weeklyNewCreators.currentPeriod}
            priorPeriod={data.creatorKPIs.weeklyNewCreators.priorPeriod}
            metricName="New Creators"
            formatValue={(v) => utilFormatNumber(v, true)}
            dateRange={dateRange}
            limits={limits}
            className="w-[388px] h-[174px]"
          />
          <MetricsChart01
            title={data.creatorKPIs.averageCreatorGMV.value}
            subtitle={data.creatorKPIs.averageCreatorGMV.description}
            change={data.creatorKPIs.averageCreatorGMV.change}
            changeDescription="vs prior period"
            actions={false}
            trend={data.creatorKPIs.averageCreatorGMV.trend === "up" ? "positive" : "negative"}
            chartData={data.creatorKPIs.averageCreatorGMV.chartData?.map((item, index, array) => ({
              week: item.week,
              value: item.value,
              highlight: index === array.length - 1,
            }))}
            tooltip={<MetricsTooltip metricType="gmv" metricName="Avg. GMV" staticTT={true} />}
            currentPeriod={data.creatorKPIs.averageCreatorGMV.currentPeriod}
            priorPeriod={data.creatorKPIs.averageCreatorGMV.priorPeriod}
            metricName="Avg. GMV"
            formatValue={(v) => utilFormatCurrency(currencySymbol, v)}
            dateRange={dateRange}
            limits={limits}
            className="w-[388px] h-[174px]"
          />
        </div>{" "}
        {/* Layout container */}
        <div className="flex-1 ">
          <TableCard.Root className={cx("self-start", "h-[364px]")} size="md">
            <Table aria-label="Top Creators" selectionMode="none">
              <Table.Header className="h-44px">
                <Table.Head label="Top Creator" className="w-2/5" isRowHeader />
                <Table.Head label="GMV" className="w-1/5" />
                <Table.Head label="% Total GMV" className="w-1/5" />
                <Table.Head label="Units sold" className="w-1/5" />
              </Table.Header>
              <Table.Body items={data.topCreators}>
                {(creator) => {
                  const id = `creator-${creator.name}`;
                  return (
                    <Table.Row id={id} key={id} className="h-[64px]">
                      <Table.Cell className="text-sm font-medium text-primary">
                        <button
                          type="button"
                          onClick={() => setSelectedCreatorId(creator.id)}
                          className="text-left underline decoration-dotted decoration-1 underline-offset-2 hover:text-brand-primary transition-colors cursor-pointer"
                        >
                          {creator.name}
                        </button>
                      </Table.Cell>
                      <Table.Cell className="text-sm font-medium text-primary">{creator.gmv}</Table.Cell>
                      <Table.Cell className="text-sm font-medium text-primary">{creator.percentage}</Table.Cell>
                      <Table.Cell className="text-sm font-medium text-primary">{creator.units}</Table.Cell>
                    </Table.Row>
                  );
                }}
              </Table.Body>
            </Table>
          </TableCard.Root>
        </div>
      </div>

      <CreatorDetails id={selectedCreatorId} onClose={() => setSelectedCreatorId(undefined)} />
    </div>
  );
};

type TimeGranularity = "day" | "week" | "month";

interface AnalyticsOverviewProps {
  timePeriod: "daily" | "weekly" | "monthly";
  dateRange?: { start: DateValue; end: DateValue } | null;
  limits?: { minDate: DateValue; maxDate: DateValue };
}

export const AnalyticsOverview: React.FC<AnalyticsOverviewProps> = ({ timePeriod, dateRange, limits }) => {
  return (
    <>
      {/* Dashboard Content */}
      <WeeklyKPIsOverview timePeriod={timePeriod} dateRange={dateRange} limits={limits} />
      <RevenueOverview timePeriod={timePeriod} dateRange={dateRange} limits={limits} />
      <CreatorsOverview timePeriod={timePeriod} dateRange={dateRange} limits={limits} />
    </>
  );
};
